import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge.jsx';
import { Input } from '@/components/ui/input.jsx';
import { useCarrierList } from '@/hooks/useCarriers.js';
import { LINE_LABELS, ROUTING_LABELS } from '@/lib/carrier-labels.js';

// Carrier admin list. Edit-only surface — carrier creation is SQL-only by
// policy, so there is intentionally no "Add carrier" button anywhere here.

const LINE_FILTERS = [
  { key: 'hull_liability', label: 'Hull & Liability' },
  { key: 'hangar', label: 'Hangar' },
];
const ACTIVE_FILTERS = [
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

export default function CarriersAdmin() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [linesOn, setLinesOn] = useState(() => new Set(LINE_FILTERS.map((f) => f.key)));
  // Inactive (soft-deleted) carriers are HIDDEN by default — a deactivated row
  // sitting in the list reads as a live market. Toggle the Inactive chip to
  // reveal them; revealed rows render dimmed with the Inactive badge.
  const [activeOn, setActiveOn] = useState(() => new Set(['active']));
  const { rows, error, loading } = useCarrierList();

  function toggleIn(setter) {
    return (key) => setter((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }
  const toggleLine = toggleIn(setLinesOn);
  const toggleActive = toggleIn(setActiveOn);

  // Line chips dim carriers whose known lines are all toggled off; carriers
  // with no hull/hangar line (property-only etc.) stay visible regardless.
  // Status chips HIDE (not dim): an off chip removes those rows entirely, and
  // inactive rows are always dimmed when revealed.
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (rows || []).map((r) => {
      const lines = Array.isArray(r.lines_of_business) ? r.lines_of_business : [];
      const known = lines.filter((l) => LINE_FILTERS.some((f) => f.key === l));
      const lineDim = known.length > 0 && !known.some((l) => linesOn.has(l));
      const statusHidden = !activeOn.has(r.active ? 'active' : 'inactive');
      const hay = [r.slug, r.name, r.routing_type, ...lines].filter(Boolean).join(' ').toLowerCase();
      return { ...r, _dim: lineDim || !r.active, _hidden: statusHidden || (!!needle && !hay.includes(needle)) };
    }).filter((r) => !r._hidden);
  }, [rows, q, linesOn, activeOn]);

  return (
    <div className="p-4">
      <div>
        <h2 className="text-xl font-semibold text-text">Carriers</h2>
        <p className="mt-1 text-sm text-text-muted">Routing, contacts &amp; dispatch recipients · carrier creation is SQL-only</p>
      </div>

      {/* Toolbar */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} strokeWidth={1.5} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <Input className="pl-9" placeholder="slug, name, line…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {LINE_FILTERS.map((f) => (
          <button key={f.key} type="button" onClick={() => toggleLine(f.key)}
            className={['rounded-pill border px-2.5 py-1 text-xs',
              linesOn.has(f.key) ? 'border-accent/50 bg-accent-soft text-accent' : 'border-border text-text-dim'].join(' ')}>
            {f.label}
          </button>
        ))}
        {ACTIVE_FILTERS.map((f) => (
          <button key={f.key} type="button" onClick={() => toggleActive(f.key)}
            className={['rounded-pill border px-2.5 py-1 text-xs',
              activeOn.has(f.key) ? 'border-accent/50 bg-accent-soft text-accent' : 'border-border text-text-dim'].join(' ')}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="mt-4">
        {loading && !rows && <div className="rounded-card border border-border bg-surface p-6 text-sm text-text-muted">Loading…</div>}
        {error && !rows && (
          <div className="rounded-card border border-accent/40 bg-accent-soft p-4 text-sm text-accent">
            Couldn't load carriers: {error.body?.error || error.code || error.message}
          </div>
        )}
        {rows && filtered.length === 0 && (
          <div className="rounded-card border border-dashed border-border p-6 text-center text-sm text-text-muted">
            No carriers{q ? ' match your search' : ''}.
          </div>
        )}
        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-card border border-border bg-surface">
            <CarrierTable rows={filtered} onRow={(slug) => navigate(`/admin/carriers/${slug}`)} />
          </div>
        )}
      </div>
    </div>
  );
}

function HeadRow({ tmpl, labels }) {
  return (
    <div className="grid gap-2 border-b border-border bg-surface-strong/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-text-dim" style={{ gridTemplateColumns: tmpl }}>
      {labels.map((l) => <span key={l}>{l}</span>)}
    </div>
  );
}

function Row({ tmpl, dim, onClick, children }) {
  return (
    <div role="button" tabIndex={0} onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      className={['grid w-full cursor-pointer items-center gap-2 border-b border-border px-3 py-2 text-left text-sm last:border-0 hover:bg-surface-strong/50',
        dim ? 'opacity-45' : ''].join(' ')}
      style={{ gridTemplateColumns: tmpl }}>
      {children}
    </div>
  );
}

function CarrierTable({ rows, onRow }) {
  const tmpl = '7rem 1.5fr 1.3fr 6.5rem 5.5rem 5.5rem';
  return (
    <div>
      <HeadRow tmpl={tmpl} labels={['Code', 'Name', 'Lines', 'Routing', 'Contacts', 'Status']} />
      {rows.map((r) => {
        const lines = Array.isArray(r.lines_of_business) ? r.lines_of_business : [];
        return (
          <Row key={r.slug} tmpl={tmpl} dim={r._dim} onClick={() => onRow(r.slug)}>
            <span className="truncate font-mono text-xs font-semibold uppercase text-text">{r.slug}</span>
            <span className="truncate font-medium text-text">{r.name}</span>
            <span className="flex flex-wrap gap-1">
              {lines.length === 0 && <span className="text-text-dim">—</span>}
              {lines.map((l) => <Badge key={l} variant="outline">{LINE_LABELS[l] || l}</Badge>)}
            </span>
            <span className="text-text-muted">{ROUTING_LABELS[r.routing_type] || r.routing_type}</span>
            <span className="text-text-dim">{(r.contacts || []).length}</span>
            <span><Badge variant={r.active ? 'success' : 'dashed'}>{r.active ? 'Active' : 'Inactive'}</Badge></span>
          </Row>
        );
      })}
    </div>
  );
}
